import { useState, useEffect, useRef, useCallback } from 'react';
import {
  logUpdaterDiagnostic,
  manualDownloadUrl,
  openManualDownloadUrl,
  parseLegacyManualCheckResult,
  parseUpdateCheckFailedPayload,
  parseUpdateErrorPayload,
  retryConnectorUpdateCheck,
} from '../utils/updaterUi';

const GOLD = '#C9A84C';
const SAFETY_TIMEOUT_MS = 20000;

const STATUS_TEXT = {
  checking: 'Checking for updates\u2026',
  downloading: 'Downloading update\u2026',
  installing: 'Installing update\u2026',
  restarting: 'Restarting Maestro\u2026',
};

function handleCheckFailedPayload(setNotice, payload) {
  const text = parseUpdateCheckFailedPayload(payload);
  logUpdaterDiagnostic('check-failed', text, { source: 'UpdateTransition' });
  setNotice({ kind: 'check-failed', message: text });
}

function handleInstallErrorPayload(setErrorPanel, payload) {
  const parsed = parseUpdateErrorPayload(payload);
  logUpdaterDiagnostic('install-error', parsed.message, {
    source: 'UpdateTransition',
    remote_version: parsed.remoteVersion || undefined,
  });
  setErrorPanel(parsed);
}

export default function UpdateTransition() {
  const [overlay, setOverlay] = useState({ visible: false, status: 'checking', newVersion: '' });
  const [notice, setNotice] = useState(null);
  const [errorPanel, setErrorPanel] = useState(null);
  const [fading, setFading] = useState(false);
  const safetyTimer = useRef(null);
  const hasSetup = useRef(false);

  const clearOverlay = useCallback(() => {
    setFading(true);
    setTimeout(() => {
      setOverlay({ visible: false, status: 'checking', newVersion: '' });
      setFading(false);
    }, 400);
  }, []);

  const resetSafetyTimer = useCallback(() => {
    if (safetyTimer.current) clearTimeout(safetyTimer.current);
    safetyTimer.current = setTimeout(clearOverlay, SAFETY_TIMEOUT_MS);
  }, [clearOverlay]);

  const dismissNotice = useCallback(() => setNotice(null), []);

  const onRetryCheck = useCallback(async () => {
    setNotice(null);
    setErrorPanel(null);
    try {
      const result = await retryConnectorUpdateCheck();
      if (typeof result === 'string' && result.trim()) {
        const legacy = parseLegacyManualCheckResult(result);
        if (legacy.status === 'check-failed') {
          handleCheckFailedPayload(setNotice, legacy.message);
        }
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e || 'Update check failed');
      handleCheckFailedPayload(setNotice, message);
    }
  }, []);

  useEffect(() => {
    if (hasSetup.current) return;
    hasSetup.current = true;

    const tauri = window.__TAURI__;
    if (!tauri?.event?.listen) return;

    const listen = tauri.event.listen.bind(tauri.event);
    const unlisteners = [];

    const setup = async () => {
      unlisteners.push(await listen('maestro://update-checking', () => {
        setNotice(null);
        setErrorPanel(null);
        setOverlay({ visible: true, status: 'checking', newVersion: '' });
        resetSafetyTimer();
      }));

      unlisteners.push(await listen('maestro://update-downloading', (e) => {
        const version = e?.payload || '';
        setNotice(null);
        setErrorPanel(null);
        setOverlay(s => ({ ...s, visible: true, status: 'downloading', newVersion: version || s.newVersion }));
        resetSafetyTimer();
      }));

      unlisteners.push(await listen('maestro://update-installing', (e) => {
        const version = e?.payload || '';
        setOverlay(s => ({ ...s, visible: true, status: 'installing', newVersion: version || s.newVersion }));
        resetSafetyTimer();
      }));

      unlisteners.push(await listen('maestro://update-restarting', (e) => {
        const version = e?.payload || '';
        setOverlay(s => ({ ...s, visible: true, status: 'restarting', newVersion: version || s.newVersion }));
        if (safetyTimer.current) clearTimeout(safetyTimer.current);
      }));

      const onUpToDate = () => {
        clearOverlay();
        setErrorPanel(null);
        setNotice(null);
      };
      unlisteners.push(await listen('maestro://update-up-to-date', onUpToDate));
      unlisteners.push(await listen('maestro://update-none', onUpToDate));

      const onCheckFailed = (e) => {
        clearOverlay();
        setErrorPanel(null);
        handleCheckFailedPayload(setNotice, e?.payload);
      };
      unlisteners.push(await listen('maestro://update-check-failed', onCheckFailed));
      unlisteners.push(await listen('maestro://update-check-error', onCheckFailed));

      unlisteners.push(await listen('maestro://update-error', (e) => {
        clearOverlay();
        setNotice(null);
        handleInstallErrorPayload(setErrorPanel, e?.payload);
      }));
    };

    setup();

    return () => {
      unlisteners.forEach(fn => fn());
      if (safetyTimer.current) clearTimeout(safetyTimer.current);
    };
  }, [clearOverlay, resetSafetyTimer]);

  const currentVersion = window.__MAESTRO_CONNECTOR__?.version || '';

  return (
    <>
      {notice && (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 999998,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
            borderRadius: 12,
            background: 'rgba(40, 20, 20, 0.96)',
            border: '1px solid rgba(248, 113, 113, 0.35)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.45)',
            maxWidth: 'min(420px, calc(100vw - 32px))',
          }}
        >
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', lineHeight: 1.4, flex: 1 }}>
            {notice.message}
          </span>
          {notice.kind === 'check-failed' && (
            <button
              type="button"
              onClick={onRetryCheck}
              style={{
                flexShrink: 0,
                background: GOLD,
                border: 'none',
                color: '#1a1a2e',
                borderRadius: 8,
                padding: '6px 10px',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          )}
          <button
            type="button"
            onClick={dismissNotice}
            style={{
              flexShrink: 0,
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'rgba(255,255,255,0.8)',
              borderRadius: 8,
              padding: '6px 10px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {errorPanel && (
        <div
          role="alertdialog"
          aria-labelledby="update-error-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 999998,
            background: 'rgba(0, 0, 0, 0.72)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <div style={{
            width: '100%',
            maxWidth: 420,
            padding: 28,
            borderRadius: 20,
            background: 'rgba(18, 18, 30, 0.95)',
            border: '1px solid rgba(248, 113, 113, 0.25)',
            boxShadow: '0 24px 64px rgba(0, 0, 0, 0.5)',
          }}>
            <p id="update-error-title" style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: '#fff' }}>
              Update could not be installed
            </p>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>
              {errorPanel.message}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <button
                type="button"
                onClick={() => {
                  setErrorPanel(null);
                  onRetryCheck();
                }}
                style={{
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: 'none',
                  background: GOLD,
                  color: '#1a1a2e',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Retry
              </button>
              <button
                type="button"
                onClick={() => openManualDownloadUrl(manualDownloadUrl(errorPanel.remoteVersion))}
                style={{
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: 'transparent',
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Download manually
              </button>
              <button
                type="button"
                onClick={() => setErrorPanel(null)}
                style={{
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: 'none',
                  background: 'transparent',
                  color: 'rgba(255,255,255,0.45)',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {overlay.visible && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 999999,
          background: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: fading ? 0 : 1,
          transition: 'opacity 0.4s ease',
        }}>
          <style>{`
            @keyframes updateFadeIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
            @keyframes updateSpin { to { transform: rotate(360deg); } }
          `}</style>

          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 32,
            padding: 48, maxWidth: 380, width: '100%',
            background: 'rgba(18, 18, 30, 0.8)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 24,
            boxShadow: '0 32px 80px rgba(0, 0, 0, 0.5)',
            animation: 'updateFadeIn 0.3s ease-out',
          }}>
            <span style={{
              fontSize: 20, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase',
              background: `linear-gradient(135deg, #fff 0%, ${GOLD} 60%, #E8D48B 100%)`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>
              Maestro
            </span>

            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              border: '3px solid rgba(255, 255, 255, 0.06)',
              borderTopColor: GOLD,
              animation: 'updateSpin 1.4s linear infinite',
            }} />

            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 17, fontWeight: 600, color: 'rgba(255,255,255,0.9)', margin: 0, lineHeight: 1.4 }}>
                {STATUS_TEXT[overlay.status] || 'Updating\u2026'}
              </p>
              {(currentVersion || overlay.newVersion) && (
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: '10px 0 0', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                  {currentVersion ? `v${currentVersion}` : ''}
                  {currentVersion && overlay.newVersion ? ' \u2192 ' : ''}
                  {overlay.newVersion ? `v${overlay.newVersion}` : ''}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
